import React, { useState, useEffect, useMemo } from 'react';
import { useForm, useFieldArray } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { Plus, Trash2, Save, X, Lock } from 'lucide-react';
import { FeeStructureFormData, SchoolClass } from '../../types';
import { schoolDataApi } from '../../services/api';
import { sortClasses } from '../../utils/classUtils';
import * as yup from 'yup';

interface FeeStructureFormProps {
  onSubmit: (data: FeeStructureFormData) => void;
  loading?: boolean;
  onCancel: () => void;
  initialData?: Partial<FeeStructureFormData>;
}

const feeStructureSchema = yup.object({
  structureId: yup
    .string()
    .required('Structure ID is required')
    .matches(/^[A-Z0-9_-]+$/, 'Structure ID must contain only uppercase letters, numbers, underscores, and hyphens'),
  className: yup
    .string()
    .required('Class is required'),
  academicYear: yup
    .string()
    .required('Academic year is required')
    .matches(/^\d{4}-\d{4}$/, 'Academic year must be in format YYYY-YYYY (e.g., 2023-2024)'),
  type: yup
    .string()
    .required('Fee type is required')
    .oneOf(['admission', 'monthly', 'annual'], 'Please select a valid fee type'),
  feeCategories: yup
    .array()
    .of(
      yup.object({
        category: yup.string().required('Category name is required'),
        amount: yup
          .number()
          .required('Amount is required')
          .positive('Amount must be positive')
          .min(1, 'Amount must be at least ₹1'),
      })
    )
    .min(1, 'Please add at least one fee category'),
  isActive: yup
    .boolean()
    .required(),
});

const typeLabels: Record<string, string> = {
  monthly: 'Monthly Fee',
  admission: 'Admission Fee',
  annual: 'Annual Fee'
};

/** e.g. ("Class 1", "monthly", "2026-2027") -> "CLASS_1_MONTHLY_2026-2027" */
const buildStructureId = (className: string, type: string, academicYear: string): string => {
  if (!className || !type || !academicYear) return '';
  const classToken = className.trim().toUpperCase().replace(/^CLASS\s+/, '');
  return `CLASS_${classToken}_${type.toUpperCase()}_${academicYear}`;
};

const FeeStructureForm: React.FC<FeeStructureFormProps> = ({
  onSubmit,
  loading = false,
  onCancel,
  initialData
}) => {
  const isEditing = !!initialData;
  const [classes, setClasses] = useState<SchoolClass[]>([]);
  const [loadingClasses, setLoadingClasses] = useState(true);
  const [academicYear, setAcademicYear] = useState(initialData?.academicYear || '');
  const [loadingYear, setLoadingYear] = useState(!initialData);

  const {
    register,
    control,
    handleSubmit,
    watch,
    setValue,
    formState: { errors }
  } = useForm({
    resolver: yupResolver(feeStructureSchema),
    defaultValues: {
      structureId: initialData?.structureId || '',
      className: initialData?.className || '',
      academicYear: initialData?.academicYear || '',
      type: initialData?.type || 'monthly',
      feeCategories: initialData?.feeCategories || [{ category: '', amount: 0 }],
      isActive: initialData?.isActive ?? true,
    }
  });

  const { fields, append, remove, update } = useFieldArray({
    control,
    name: 'feeCategories'
  });

  const watchedCategories = watch('feeCategories');
  const watchedClassName = watch('className');
  const watchedType = watch('type');

  const totalAmount = watchedCategories?.reduce((sum, category) => sum + (category.amount || 0), 0) || 0;

  useEffect(() => {
    loadClasses();
    if (!initialData) {
      schoolDataApi.settings.getAcademicYear()
        .then(year => {
          setAcademicYear(year);
          setValue('academicYear', year);
        })
        .catch(() => {})
        .finally(() => setLoadingYear(false));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // The structure ID is entirely derived — never typed by the user — and is
  // frozen once a structure exists (editing className/type/year afterwards
  // would desync it from the immutable ID, so those fields are locked too).
  useEffect(() => {
    if (isEditing) return;
    const generated = buildStructureId(watchedClassName, watchedType, academicYear);
    setValue('structureId', generated);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watchedClassName, watchedType, academicYear, isEditing]);

  const loadClasses = async () => {
    try {
      setLoadingClasses(true);
      const data = await schoolDataApi.classes.getAll();
      const activeClasses = data.filter(cls => cls.isActive);
      const sortedClasses = sortClasses(activeClasses);
      setClasses(sortedClasses);
    } catch (error) {
      console.error('Error loading classes:', error);
      setClasses([]);
    } finally {
      setLoadingClasses(false);
    }
  };

  const addCategory = () => {
    append({ category: '', amount: 0 });
  };

  const removeCategory = (index: number) => {
    if (fields.length > 1) {
      remove(index);
    }
  };

  const handleFormSubmit = (data: any) => {
    onSubmit(data as FeeStructureFormData);
  };

  const suggestedCategories = useMemo(() => ({
    admission: [
      'Admission Fee',
      'Registration Fee',
      'Caution Money',
      'Prospectus Fee',
      'Application Fee'
    ],
    monthly: [
      'Tuition Fee',
      'Library Fee',
      'Laboratory Fee',
      'Sports Fee',
      'Transport Fee',
      'Hostel Fee',
      'Examination Fee',
      'Development Fee'
    ],
    annual: [
      'Annual Charges',
      'Infrastructure Fee',
      'Examination Fee',
      'Sports Fee',
      'Development Fee'
    ]
  }), []);

  return (
    <div className="bg-[#211c15] rounded-xl border border-[#3a3122]">
      <div className="p-6 border-b border-[#3a3122]">
        <h2 className="text-lg font-semibold text-[#f3ede2]">
          {isEditing ? 'Edit Fee Structure' : 'Create Fee Structure'}
        </h2>
        <p className="text-[#b9ad9b] text-sm mt-1">
          Set up {watchedType} fee structure for students
        </p>
      </div>

      <form onSubmit={handleSubmit(handleFormSubmit)} className="p-6 space-y-6">
        {/* Basic Information */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-[#b9ad9b] mb-2">
              Fee Type *
            </label>
            {isEditing ? (
              <>
                <div className="bg-[#28221a] text-[#8a7f6e] w-full px-3 py-2 border border-[#3a3122] rounded-lg cursor-not-allowed">
                  {typeLabels[watchedType] || watchedType}
                </div>
                <input type="hidden" {...register('type')} />
              </>
            ) : (
              <select
                {...register('type')}
                className="bg-[#17140f] text-[#f3ede2] placeholder-[#8a7f6e] w-full px-3 py-2 border border-[#3a3122] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#e2916d] focus:border-transparent"
              >
                <option value="monthly">Monthly Fee</option>
                <option value="admission">Admission Fee</option>
                <option value="annual">Annual Fee</option>
              </select>
            )}
            {errors.type && (
              <p className="text-[#f38b83] text-sm mt-1">{errors.type.message}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-[#b9ad9b] mb-2">
              Class *
            </label>
            {isEditing ? (
              <>
                <div className="bg-[#28221a] text-[#8a7f6e] w-full px-3 py-2 border border-[#3a3122] rounded-lg cursor-not-allowed">
                  {watchedClassName}
                </div>
                <input type="hidden" {...register('className')} />
              </>
            ) : (
              <select
                {...register('className')}
                disabled={loadingClasses}
                className="bg-[#17140f] text-[#f3ede2] placeholder-[#8a7f6e] w-full px-3 py-2 border border-[#3a3122] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#e2916d] focus:border-transparent disabled:bg-[#28221a] disabled:cursor-not-allowed disabled:text-[#8a7f6e]"
              >
                <option value="">{loadingClasses ? 'Loading classes...' : 'Select Class'}</option>
                {classes.map((cls) => (
                  <option key={cls.id} value={cls.name}>
                    {cls.name}
                  </option>
                ))}
              </select>
            )}
            {errors.className && (
              <p className="text-[#f38b83] text-sm mt-1">{errors.className.message}</p>
            )}
            {!isEditing && !loadingClasses && classes.length === 0 && (
              <p className="text-[#f0b64c] text-sm mt-1">
                No active classes found. Please add classes in Settings → Class Management first.
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-[#b9ad9b] mb-2">
              Academic Year *
            </label>
            <input
              type="text"
              readOnly
              value={loadingYear ? 'Loading...' : academicYear}
              className="bg-[#28221a] text-[#8a7f6e] w-full px-3 py-2 border border-[#3a3122] rounded-lg cursor-not-allowed"
            />
            <input type="hidden" {...register('academicYear')} />
            <p className="text-[#8a7f6e] text-xs mt-1">
              Set once for the whole school in Settings → Academic Year.
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-[#b9ad9b] mb-2 flex items-center gap-1.5">
              <Lock className="w-3.5 h-3.5" />
              Structure ID *
            </label>
            <input
              type="text"
              readOnly
              {...register('structureId')}
              placeholder="Select a class, type and year first"
              className="bg-[#28221a] text-[#8a7f6e] w-full px-3 py-2 border border-[#3a3122] rounded-lg cursor-not-allowed"
            />
            {errors.structureId && (
              <p className="text-[#f38b83] text-sm mt-1">{errors.structureId.message}</p>
            )}
            <p className="text-[#8a7f6e] text-xs mt-1">Generated automatically — cannot be edited.</p>
          </div>
        </div>

        {/* Fee Categories */}
        <div>
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-[#f3ede2]">Fee Categories</h3>
            <button
              type="button"
              onClick={addCategory}
              className="flex items-center space-x-2 px-3 py-2 text-[#e2916d] border border-[#e2916d] rounded-lg hover:bg-[#e2916d] hover:text-white transition-colors"
            >
              <Plus className="w-4 h-4" />
              <span>Add Category</span>
            </button>
          </div>

          {/* Suggested Categories */}
          <div className="mb-4 p-4 bg-[#28221a] rounded-lg">
            <h4 className="text-sm font-medium text-[#b9ad9b] mb-2">
              Suggested {watchedType} fee categories:
            </h4>
            <div className="flex flex-wrap gap-2">
              {suggestedCategories[watchedType as keyof typeof suggestedCategories]?.map((category) => (
                <button
                  key={category}
                  type="button"
                  onClick={() => {
                    const emptyIndex = watchedCategories.findIndex(cat => !cat.category);
                    if (emptyIndex !== -1) {
                      update(emptyIndex, { ...watchedCategories[emptyIndex], category });
                    } else {
                      append({ category, amount: 0 });
                    }
                  }}
                  className="px-3 py-1 text-sm bg-[#211c15] border border-[#3a3122] rounded-full hover:bg-[#e2916d] hover:text-white hover:border-[#e2916d] transition-colors"
                >
                  {category}
                </button>
              ))}
            </div>
          </div>

          <div className="overflow-x-auto border border-[#3a3122] rounded-lg">
            <table className="w-full">
              <thead className="bg-[#28221a]">
                <tr>
                  <th className="text-left py-2.5 px-4 text-xs font-medium text-[#8a7f6e] uppercase">Category</th>
                  <th className="text-right py-2.5 px-4 text-xs font-medium text-[#8a7f6e] uppercase w-48">Amount (₹)</th>
                  <th className="w-12"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#3a3122]">
                {fields.map((field, index) => (
                  <tr key={field.id}>
                    <td className="px-4 py-2">
                      <input
                        type="text"
                        {...register(`feeCategories.${index}.category`)}
                        placeholder="e.g., Tuition Fee"
                        className="bg-transparent text-[#f3ede2] placeholder-[#8a7f6e] w-full px-2 py-1.5 border border-transparent hover:border-[#3a3122] focus:border-[#e2916d] rounded focus:outline-none transition-colors"
                      />
                      {errors.feeCategories?.[index]?.category && (
                        <p className="text-[#f38b83] text-xs mt-0.5">
                          {errors.feeCategories[index]?.category?.message}
                        </p>
                      )}
                    </td>
                    <td className="px-4 py-2">
                      <input
                        type="number"
                        {...register(`feeCategories.${index}.amount`, { valueAsNumber: true })}
                        placeholder="0"
                        min="0"
                        step="1"
                        className="bg-transparent text-[#f3ede2] placeholder-[#8a7f6e] w-full px-2 py-1.5 border border-transparent hover:border-[#3a3122] focus:border-[#e2916d] rounded focus:outline-none text-right transition-colors"
                      />
                      {errors.feeCategories?.[index]?.amount && (
                        <p className="text-[#f38b83] text-xs mt-0.5 text-right">
                          {errors.feeCategories[index]?.amount?.message}
                        </p>
                      )}
                    </td>
                    <td className="px-2 py-2 text-center">
                      {fields.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeCategory(index)}
                          className="p-1.5 text-[#8a7f6e] hover:text-[#f38b83] hover:bg-[#341a17] rounded-lg transition-colors"
                          title="Remove category"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="border-t-2 border-[#e2916d] bg-[#28221a]">
                  <td className="px-4 py-3 text-[#f3ede2] font-medium">Total</td>
                  <td className="px-4 py-3 text-right text-[#e2916d] font-bold">₹{totalAmount.toLocaleString()}</td>
                  <td></td>
                </tr>
              </tfoot>
            </table>
          </div>

          {errors.feeCategories?.root && (
            <p className="text-[#f38b83] text-sm mt-2">{errors.feeCategories.root.message}</p>
          )}
        </div>

        {/* Status */}
        <div className="flex items-center space-x-2">
          <input
            type="checkbox"
            {...register('isActive')}
            id="isActive"
            className="w-4 h-4 text-[#e2916d] border-[#3a3122] rounded focus:ring-[#e2916d]"
          />
          <label htmlFor="isActive" className="text-sm font-medium text-[#b9ad9b]">
            Active (Available for assignment to students)
          </label>
        </div>

        {/* Actions */}
        <div className="flex items-center justify-end space-x-3 pt-6 border-t border-[#3a3122]">
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 text-[#b9ad9b] border border-[#3a3122] rounded-lg hover:bg-[#28221a] transition-colors"
          >
            <X className="w-4 h-4 inline-block mr-2" />
            Cancel
          </button>
          <button
            type="submit"
            disabled={loading}
            className="flex items-center space-x-2 px-4 py-2 bg-[#e2916d] hover:bg-[#f0a67d] disabled:bg-[#8a7f6e] disabled:cursor-not-allowed text-white rounded-lg transition-colors"
          >
            <Save className="w-4 h-4" />
            <span>{loading ? 'Saving...' : isEditing ? 'Update' : 'Create'} Fee Structure</span>
          </button>
        </div>
      </form>
    </div>
  );
};

export default FeeStructureForm;
